// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  CHARGE_CANCELLATION_REASON_MAX_LENGTH,
  ChargeListItem,
} from '@/billing/api/types';
import { ChargePreviewCard } from '@/billing/components/charge-preview-card';
import { useCancelCharge } from '@/billing/hooks/use-cancel-charge';
import { Warning as WarningIcon } from '@/constants/hardfin-standard-icons';
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';

interface Props {
  isOpen: boolean;
  charge: ChargeListItem | null;
  onClose: () => void;
  onError?: (
    message: string,
    apiStatusCode: number,
    httpStatusCode: number
  ) => void;
}

/**
 * Confirms cancellation of a single charge. Cancellation is destructive and
 * cannot be undone, so the user must explicitly acknowledge the warning.
 * Users may optionally supply a reason that is persisted on the charge and
 * recorded in the audit log.
 */
export function CancelChargeDialog(props: Props) {
  const [reason, setReason] = useState<string>('');

  const close = () => {
    props.onClose();
    setReason('');
  };

  const cancelMutation = useCancelCharge({
    onSuccess: () => {
      close();
    },
    onError: (message, apiStatusCode, httpStatusCode) => {
      close();
      props.onError?.(message, apiStatusCode, httpStatusCode);
    },
  });

  const handleConfirm = () => {
    if (!props.charge) {
      return;
    }
    // Trim and treat empty input as null so the backend records "no reason
    // provided" rather than an empty string that would still trip the
    // audit-when-present rule.
    const trimmed = reason.trim();
    cancelMutation.mutate({
      chargeKey: props.charge.key,
      cancellationReason: trimmed.length === 0 ? null : trimmed,
    });
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={props.isOpen} onClose={close}>
      <DialogTitle
        sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 2 }}
      >
        Cancel charge
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Alert
          severity="warning"
          icon={<WarningIcon fontSize="medium" />}
          sx={{ marginY: 2, alignItems: 'center' }}
        >
          <AlertTitle variant="h6">
            Cancel charge <strong>{props.charge?.label ?? ''}</strong>?
          </AlertTitle>
          <Typography variant="body1">
            The charge will not be billed and will be excluded from future
            invoices
          </Typography>
        </Alert>

        {props.charge && <ChargePreviewCard charge={props.charge} />}

        <TextField
          label="Reason (optional)"
          placeholder="Why is this charge being cancelled?"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={cancelMutation.isPending}
          inputProps={{ maxLength: CHARGE_CANCELLATION_REASON_MAX_LENGTH }}
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          sx={{ mt: 1 }}
        />
      </DialogContent>

      <DialogActions
        sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}
      >
        <Button
          onClick={close}
          variant="outlined"
          disabled={cancelMutation.isPending}
        >
          No, keep charge
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={cancelMutation.isPending || props.charge === null}
          sx={{ ml: 1.5 }}
        >
          Yes, cancel charge
        </Button>
      </DialogActions>
    </Dialog>
  );
}
